import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle, FiClock, FiShield, FiArrowRight, FiZap, FiUsers, FiHeart, FiUser, FiBriefcase, FiHome, FiCalendar, FiDollarSign, FiTrendingUp, FiX } = FiIcons;

const NoMedicalExamPage = () => {
  const benefits = [
    'No medical exam, blood tests, or urine tests',
    'Coverage up to $1 million available',
    'Instant approval for qualifying applicants',
    'Apply online in just 5 minutes',
    'Answer only basic health questions',
    'Same-day coverage available'
  ];

  const whoShouldConsider = [
    {
      icon: FiClock,
      title: 'Busy Professionals',
      description: 'No time for medical appointments or lengthy processes',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      scenarios: [
        'Frequent travelers',
        'Demanding work schedules',
        'Need coverage immediately'
      ]
    },
    {
      icon: FiHeart,
      title: 'Health-Conscious Applicants',
      description: 'Minor health issues that complicate traditional underwriting',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      scenarios: [
        'Anxiety about medical procedures',
        'Minor health conditions',
        'Previous insurance denials'
      ]
    },
    {
      icon: FiZap,
      title: 'Urgent Coverage Needs',
      description: 'Life changes requiring immediate protection',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      scenarios: [
        'New mortgage or loan',
        'Recent marriage/birth',
        'Business obligations'
      ]
    },
    {
      icon: FiUsers,
      title: 'Younger Applicants (25-45)',
      description: 'Healthy individuals who qualify for simplified underwriting',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      scenarios: [
        'Generally healthy',
        'No major medical history',
        'Want convenience'
      ]
    }
  ];

  const whoShouldNotConsider = [
    {
      icon: FiDollarSign,
      title: 'Budget-First Shoppers',
      description: 'Seeking the absolute lowest premiums possible',
      reason: 'Higher Premiums',
      alternative: 'Traditional underwriting offers better rates'
    },
    {
      icon: FiTrendingUp,
      title: 'High Coverage Seekers (>$1M)',
      description: 'Need more than $1 million in coverage',
      reason: 'Coverage Limits',
      alternative: 'Traditional policies offer higher amounts'
    },
    {
      icon: FiUser,
      title: 'Seniors (65+)',
      description: 'Limited options and higher costs for older applicants',
      reason: 'Age Restrictions',
      alternative: 'Final expense or guaranteed issue policies'
    },
    {
      icon: FiCalendar,
      title: 'Long-Term Planners',
      description: 'Have time for thorough medical underwriting',
      reason: 'No Rush Needed',
      alternative: 'Full underwriting for better rates and options'
    }
  ];

  const idealFor = [
    {
      icon: FiClock,
      title: 'Busy Professionals',
      description: 'No time for medical exams? Get coverage quickly without disrupting your schedule.'
    },
    {
      icon: FiHeart,
      title: 'Health Concerns',
      description: 'Minor health issues that might complicate traditional underwriting? We can help.'
    },
    {
      icon: FiZap,
      title: 'Need Coverage Fast',
      description: 'Life changes happen quickly. Get the protection you need without delays.'
    }
  ];

  const testimonials = [
    {
      name: 'Jennifer C.',
      age: 39,
      location: 'San Jose, CA',
      quote: 'I was dreading the medical exam process, but this was so much easier. Got approved instantly and had coverage the same day I applied.',
      coverage: '$500,000 No Medical Exam'
    },
    {
      name: 'Mark D.',
      age: 42,
      location: 'Orange County, CA',
      quote: 'With my busy travel schedule, finding time for a medical exam was impossible. This online process was perfect for my lifestyle.',
      coverage: '$750,000 No Medical Exam'
    },
    {
      name: 'Sandra L.',
      age: 35,
      location: 'San Francisco, CA',
      quote: 'I have anxiety about medical procedures, so the no-exam option was exactly what I needed. Simple questions and instant approval.',
      coverage: '$300,000 No Medical Exam'
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Answer Health Questions',
      description: 'Simple online questionnaire about your health history and lifestyle.'
    },
    {
      step: 2,
      title: 'Instant Review',
      description: 'Our system reviews your application and provides an instant decision.'
    },
    {
      step: 3,
      title: 'Get Coverage',
      description: 'Approved applicants can activate coverage immediately after payment.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-600 to-orange-800 pt-24 pb-16 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiCheckCircle} className="h-8 w-8 mr-3" />
                <span className="font-semibold">No Medical Exam Life Insurance</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Life Insurance Without the Hassle
              </h1>
              <p className="text-xl text-orange-100 mb-8">
                Skip the medical exam and get up to $1 million in life insurance coverage. Apply online in minutes with instant approval for qualifying applicants.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://quote.burbankinsuranceservices.com/Dt2tCn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-colors"
                >
                  Get My Price
                  <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
                </a>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-white mt-8">
                <div className="flex items-center">
                  <SafeIcon icon={FiClock} className="h-5 w-5 text-yellow-300 mr-2" />
                  <span className="text-sm">5-minute application</span>
                </div>
                <div className="flex items-center">
                  <SafeIcon icon={FiZap} className="h-5 w-5 text-yellow-300 mr-2" />
                  <span className="text-sm">Instant approval</span>
                </div>
                <div className="flex items-center">
                  <SafeIcon icon={FiShield} className="h-5 w-5 text-yellow-300 mr-2" />
                  <span className="text-sm">Up to $1M coverage</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg p-8 text-secondary-900"
            >
              <h3 className="text-2xl font-bold mb-6">No Medical Exam Means:</h3>
              <div className="space-y-4">
                <div className="flex items-center p-3 bg-orange-50 rounded-lg">
                  <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-green-500 mr-3" />
                  <span>No doctor visits required</span>
                </div>
                <div className="flex items-center p-3 bg-orange-50 rounded-lg">
                  <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-green-500 mr-3" />
                  <span>No blood or urine tests</span>
                </div>
                <div className="flex items-center p-3 bg-orange-50 rounded-lg">
                  <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-green-500 mr-3" />
                  <span>No waiting for results</span>
                </div>
                <div className="flex items-center p-3 bg-orange-50 rounded-lg">
                  <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-green-500 mr-3" />
                  <span>No scheduling appointments</span>
                </div>
                <div className="flex items-center p-3 bg-orange-50 rounded-lg">
                  <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-green-500 mr-3" />
                  <span>No invasive procedures</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Should Consider No Medical Exam - Infographic */}
      <section className="py-16 bg-gradient-to-br from-secondary-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Is No Medical Exam Insurance Right for You?
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              No medical exam insurance is perfect for specific situations. Here's who benefits most and who should consider traditional underwriting instead.
            </p>
          </motion.div>

          {/* Who SHOULD Consider */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl font-bold text-green-700 mb-2">✅ Perfect Candidates for No Medical Exam</h3>
              <p className="text-secondary-600">These situations make no medical exam insurance an excellent choice</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whoShouldConsider.map((candidate, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`${candidate.bgColor} ${candidate.borderColor} border-2 rounded-xl p-6 hover:shadow-lg transition-shadow`}
                >
                  <div className="text-center mb-4">
                    <div className={`${candidate.bgColor} rounded-full p-3 w-16 h-16 mx-auto mb-3 flex items-center justify-center border ${candidate.borderColor}`}>
                      <SafeIcon icon={candidate.icon} className={`h-8 w-8 ${candidate.color}`} />
                    </div>
                    <h4 className="font-bold text-secondary-900 mb-2">{candidate.title}</h4>
                    <p className="text-sm text-secondary-600 mb-4">{candidate.description}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-secondary-800 mb-2 text-sm">Perfect for:</h5>
                    <ul className="space-y-1">
                      {candidate.scenarios.map((scenario, idx) => (
                        <li key={idx} className="text-xs text-secondary-700 flex items-center">
                          <SafeIcon icon={FiCheckCircle} className={`h-3 w-3 ${candidate.color} mr-2`} />
                          {scenario}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Who Should NOT Consider */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl font-bold text-red-700 mb-2">❌ Better Alternatives Available</h3>
              <p className="text-secondary-600">These situations typically benefit from traditional underwriting</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whoShouldNotConsider.map((candidate, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-red-50 border-2 border-red-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="text-center mb-4">
                    <div className="bg-red-100 rounded-full p-3 w-16 h-16 mx-auto mb-3 flex items-center justify-center border border-red-200">
                      <SafeIcon icon={candidate.icon} className="h-8 w-8 text-red-600" />
                    </div>
                    <h4 className="font-bold text-secondary-900 mb-2">{candidate.title}</h4>
                    <p className="text-sm text-secondary-600 mb-3">{candidate.description}</p>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-red-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-red-800">Why Not:</p>
                      <p className="text-xs text-red-700">{candidate.reason}</p>
                    </div>
                    <div className="bg-green-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-green-800">Better Option:</p>
                      <p className="text-xs text-green-700">{candidate.alternative}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Decision Helper */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-white rounded-xl shadow-lg p-8"
          >
            <h3 className="text-2xl font-bold text-secondary-900 text-center mb-8">
              Quick Decision Guide
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="text-center">
                <div className="bg-green-100 rounded-lg p-6 mb-4">
                  <SafeIcon icon={FiCheckCircle} className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  <h4 className="font-bold text-green-800 mb-2">Choose No Medical Exam If:</h4>
                  <ul className="text-sm text-green-700 space-y-1 text-left">
                    <li>• You need coverage within days</li>
                    <li>• You're too busy for appointments</li>
                    <li>• You have medical exam anxiety</li>
                    <li>• You need up to $1M coverage</li>
                    <li>• You're generally healthy (25-55)</li>
                  </ul>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-blue-100 rounded-lg p-6 mb-4">
                  <SafeIcon icon={FiClock} className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                  <h4 className="font-bold text-blue-800 mb-2">Choose Traditional Underwriting If:</h4>
                  <ul className="text-sm text-blue-700 space-y-1 text-left">
                    <li>• You want the lowest premiums</li>
                    <li>• You need over $1M coverage</li>
                    <li>• You have time for the process</li>
                    <li>• You're in excellent health</li>
                    <li>• You're shopping for best rates</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-secondary-900 mb-6">
                Why Choose No Medical Exam Life Insurance?
              </h2>
              <p className="text-lg text-secondary-600 mb-8">
                Traditional life insurance often requires medical exams that can be time-consuming, invasive, and stressful. Our no-exam policies eliminate these barriers while still providing substantial coverage.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500 mr-3 mt-1" />
                    <span className="text-secondary-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-secondary-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-secondary-900 mb-6">Application Process</h3>
              <div className="space-y-6">
                {processSteps.map((step, index) => (
                  <div key={index} className="flex items-start">
                    <div className="bg-orange-600 text-white rounded-full p-2 w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-sm font-bold">{step.step}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-secondary-900 mb-1">{step.title}</h4>
                      <p className="text-sm text-secondary-600">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-orange-800">
                  <strong>Average completion time:</strong> 5 minutes or less
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal For Section */}
      <section className="py-16 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-secondary-900 text-center mb-12">
            Perfect for These Situations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {idealFor.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 text-center"
              >
                <div className="bg-orange-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <SafeIcon icon={item.icon} className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-3">{item.title}</h3>
                <p className="text-secondary-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Options */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-secondary-900 text-center mb-12">
            Coverage Options Available
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {['$100,000', '$250,000', '$500,000', '$1,000,000'].map((amount, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center hover:shadow-lg transition-shadow"
              >
                <h3 className="text-2xl font-bold text-orange-600 mb-2">{amount}</h3>
                <p className="text-secondary-600 mb-4">Coverage Amount</p>
                <div className="text-sm text-secondary-700">
                  <p>No medical exam</p>
                  <p>Instant approval available</p>
                  <p>Fixed premiums</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-secondary-900 text-center mb-12">
            What Our No-Exam Customers Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg p-6"
              >
                <blockquote className="text-secondary-700 mb-4 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="border-t border-secondary-200 pt-4">
                  <p className="font-semibold text-secondary-900">{testimonial.name}</p>
                  <p className="text-sm text-secondary-600">{testimonial.location}</p>
                  <p className="text-sm text-orange-600 font-medium mt-1">{testimonial.coverage}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Skip the Medical Exam?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Get life insurance coverage up to $1 million without the hassle of medical exams.
          </p>
          <a
            href="https://quote.burbankinsuranceservices.com/Dt2tCn"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-colors"
          >
            Get My Price
            <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
          </a>
          <p className="mt-4 text-sm text-orange-200">
            5-minute application • Instant approval • Same-day coverage available
          </p>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default NoMedicalExamPage;